<script>
import { MESSAGE_TYPE } from 'widget/helpers/constants';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { ATTACHMENT_ICONS } from 'shared/constants/messages';
import BubbleImageAudioVideo from './bubble/ImageAudioVideo.vue';
import InstagramStory from './bubble/InstagramStory.vue';
import BubbleLocation from './bubble/Location.vue';
import BubbleContact from './bubble/Contact.vue';
import BubbleFile from './bubble/File.vue';
import BubblePix from './bubble/Pix.vue';

// Telefone do remetente no comeco da previa de mensagem de grupo.
//
// O webhook do uazapi grava o autor dentro do proprio conteudo, no formato
// `**Nome (+557998144718)**\n\n<mensagem>` (webhook_service.rb:181,193). No card
// a previa e uma linha so, truncada: o numero come metade do espaco e empurra
// para fora justamente o que a pessoa escreveu, que e o que se le numa lista.
//
// Some so na previa curta. Na conversa aberta e no ReplyTo o numero fica, que e
// onde ele serve para desambiguar quem falou num grupo.
//
// Ancorado no inicio e exigindo o `+` para nao tocar em telefone escrito no
// corpo da mensagem: "Ligue (11) 4002-8922" passa intacto. Quando nao ha nome,
// o backend poe so o numero -- ai nao ha parenteses e nada e removido, que e o
// certo: o numero E a identificacao.
const GROUP_SENDER_PHONE = /^([^\n(]*?)\s*\(\+[\d\s()-]+\)\s*/;

// `Nome (+55…)\n\nmensagem` vira `~Nome: mensagem`.
//
// O `~` segue a convencao do proprio WhatsApp para nome que nao veio da sua
// agenda. Aqui ele vale para TODO participante de grupo, e isso e honesto com o
// dado: o nome nesta posicao e sempre o `senderName` do uazapi, que e o push
// name -- o nome que a pessoa pos no proprio perfil. A mensagem nao carrega
// nenhum sinal de contato salvo, e o store de contatos indexa por id e so tem
// quem ja foi carregado na tela, entao consultar por telefone daria falso
// negativo para quase todo mundo.
//
// Os dois pontos separam quem falou do que foi dito: na previa a linha e uma so
// e, sem eles, nome e mensagem viram uma frase confusa ("Brenno Phelipe Ola").
//
// Sem conteudo depois (mensagem so de anexo, por exemplo) o nome fica sozinho:
// dois pontos apontando para o nada leem pior que nada.
const withSenderSeparator = text => {
  const match = text.match(GROUP_SENDER_PHONE);
  if (!match) return text;

  const name = `~${match[1].trim()}`;
  const body = text.slice(match[0].length).trim();

  return body ? `${name}: ${body}` : name;
};

export default {
  name: 'MessagePreview',
  components: {
    BubbleImageAudioVideo,
    InstagramStory,
    BubbleLocation,
    BubbleContact,
    BubbleFile,
    BubblePix,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    showMessageType: {
      type: Boolean,
      default: true,
    },
    defaultEmptyMessage: {
      type: String,
      default: '',
    },
    short: {
      type: Boolean,
      default: true,
    },
  },
  setup() {
    const { getPlainText } = useMessageFormatter();
    return {
      getPlainText,
    };
  },
  data: () => {
    return {
      previewMessage: null,
    };
  },
  computed: {
    contentAttributes() {
      return this.message.content_attributes || {};
    },
    isAnInstagramStory() {
      return this.contentAttributes.image_type === 'story_mention';
    },
    attachments() {
      return this.message?.attachments || [];
    },
    messageByAgent() {
      const { message_type: messageType } = this.message;
      return messageType === MESSAGE_TYPE.OUTGOING;
    },
    isMessageAnActivity() {
      const { message_type: messageType } = this.message;
      return messageType === MESSAGE_TYPE.ACTIVITY;
    },
    isMessagePrivate() {
      const { private: isPrivate } = this.message;
      return isPrivate;
    },
    // O cabecalho de remetente so existe quando o backend o prefixou, e ele faz
    // isso numa condicao unica: `is_group && !from_me`
    // (webhook_service.rb:191-193). Na mesma linha ele grava
    // `group_participant_id`, entao a presenca desse campo E o sinal exato.
    //
    // Antes o corte olhava so para `short`, que vale `true` por padrao em toda
    // previa da lista. Qualquer mensagem 1:1 comecando com `texto (+digitos)`
    // era reescrita como se fosse de grupo: um cliente mandando
    // "Meu novo numero (+55 11 98888-7777) anota ai" virava
    // "~Meu novo numero: anota ai" -- o `~` afirmando uma autoria que nao
    // existe, e o telefone que ele queria passar apagado da previa.
    isGroupParticipantMessage() {
      return Boolean(this.contentAttributes.group_participant_id);
    },
    parsedLastMessage() {
      const text = this.getPlainText(this.subject + this.message.content);

      return this.short && this.isGroupParticipantMessage
        ? withSenderSeparator(text)
        : text;
    },
    subject() {
      const { content_attributes: contentAttributes } = this.message;
      const { email: { subject } = {} } = contentAttributes || {};
      // return this.getPlainText(subject || this.message.content);
      return subject ? subject + ' - ' : '';
    },
    lastMessageFileType() {
      const [{ file_type: fileType } = {}] = this.message.attachments;
      return fileType;
    },
    attachmentIcon() {
      return ATTACHMENT_ICONS[this.lastMessageFileType];
    },
    attachmentMessageContent() {
      return `CHAT_LIST.ATTACHMENTS.${this.lastMessageFileType}.CONTENT`;
    },
    isMessageSticker() {
      return this.message && this.message.content_type === 'sticker';
    },
    isPixMessage() {
      return (
        this.contentAttributes &&
        this.contentAttributes.pix_key &&
        this.message.content_type !== 'sticker'
      );
    },
    isLocationMessage() {
      return this.contentAttributes && this.contentAttributes.location_payload;
    },
    isContactMessage() {
      return this.contentAttributes && this.contentAttributes.contact_payload;
    },
    isPollMessage() {
      return this.contentAttributes && this.contentAttributes.poll;
    },
  },
  watch: {
    data() {
      this.hasMediaLoadError = false;
    },
  },
  mounted() {
    this.hasMediaLoadError = false;
  },
  methods: {
    isAttachmentImageVideoAudio(fileType) {
      return ['image', 'audio', 'video', 'story_mention', 'ig_reel'].includes(
        fileType
      );
    },
    onMediaLoadError() {
      this.hasMediaLoadError = true;
    },
  },
};
</script>

<template>
  <div class="overflow-hidden text-ellipsis whitespace-nowrap">
    <template v-if="showMessageType">
      <fluent-icon
        v-if="isMessagePrivate"
        size="16"
        class="-mt-0.5 align-middle text-muted inline-block"
        icon="lock-closed"
      />
      <fluent-icon
        v-else-if="messageByAgent"
        size="16"
        class="-mt-0.5 align-middle text-muted inline-block"
        icon="arrow-reply"
      />
      <fluent-icon
        v-else-if="isMessageAnActivity"
        size="16"
        class="-mt-0.5 align-middle text-muted inline-block"
        icon="info"
      />
    </template>
    <span v-if="isMessageSticker">
      <fluent-icon
        size="16"
        class="-mt-0.5 align-middle inline-block text-muted"
        icon="emoji"
      />
      {{ $t('CHAT_LIST.ATTACHMENTS.sticker.CONTENT') }}
    </span>
    <span v-else-if="isPixMessage && short">
      <fluent-icon
        size="16"
        class="-mt-0.5 align-middle inline-block text-muted"
        icon="credit-card-person"
      />
      Pix
    </span>
    <BubblePix
      v-else-if="isPixMessage"
      :pix-key="contentAttributes.pix_key"
      :merchant="contentAttributes.pix_merchant"
    />
    <span v-else-if="isLocationMessage && short">
      <fluent-icon
        size="16"
        class="-mt-0.5 align-middle inline-block text-muted"
        icon="location"
      />
      {{ $t('CHAT_LIST.ATTACHMENTS.location.CONTENT') }}
    </span>
    <span v-else-if="isContactMessage && short">
      <fluent-icon
        size="16"
        class="-mt-0.5 align-middle inline-block text-muted"
        icon="person"
      />
      {{ $t('CHAT_LIST.ATTACHMENTS.contact.CONTENT') }}
    </span>
    <span v-else-if="isPollMessage && short">
      <fluent-icon
        size="16"
        class="-mt-0.5 align-middle inline-block text-muted"
        icon="list"
      />
      {{ $t('CHAT_LIST.ATTACHMENTS.poll.CONTENT') }}
    </span>
    <span v-else-if="message.content">
      {{ parsedLastMessage }}
    </span>
    <span v-else-if="message.attachments">
      <!-- `span` e nao `div`: o `div` era o unico ramo curto em bloco (sticker,
           pix, localizacao, contato e enquete ja eram `span`), e um bloco
           dentro do inline abre linha propria. Na lista, o icone de origem da
           mensagem ficava sozinho numa linha e "Mensagem com imagem" caia na
           seguinte, furando o `whitespace-nowrap` do container. -->
      <span v-if="short">
        <fluent-icon
          v-if="attachmentIcon && showMessageType"
          size="16"
          class="-mt-0.5 align-middle inline-block text-muted"
          :icon="attachmentIcon"
        />
        {{ $t(`${attachmentMessageContent}`) }}
      </span>
      <!-- Aqui continua bloco de proposito: com `short` falso o preview
           renderiza as bolhas de midia (ReplyTo), que sao blocos mesmo. -->
      <div v-else>
        <div v-for="attachment in attachments" :key="attachment.id">
          <InstagramStory
            v-if="isAnInstagramStory"
            :story-url="attachment.thumb_url"
            @error="onMediaLoadError"
          />
          <BubbleImageAudioVideo
            v-else-if="isAttachmentImageVideoAudio(attachment.file_type)"
            :attachment="attachment"
            url_type="thumb_url"
            @error="onMediaLoadError"
          />
          <BubbleLocation
            v-else-if="attachment.file_type === 'location'"
            :latitude="attachment.coordinates_lat"
            :longitude="attachment.coordinates_long"
            :name="attachment.fallback_title"
          />
          <BubbleContact
            v-else-if="attachment.file_type === 'contact'"
            :name="message.content"
            :phone-number="attachment.fallback_title"
          />
          <BubbleFile v-else :url="attachment.thumb_url" />
        </div>
      </div>
    </span>
    <span v-else>
      {{ defaultEmptyMessage || $t('CHAT_LIST.NO_CONTENT') }}
    </span>
  </div>
</template>
